import { ACCESS_TOKEN_KEY, getEnv } from '@src/core/config';
import Axios from 'axios';
import { routesConfig } from '@src/core/routes';

export const serializeQueryParams = (params: Record<string, unknown> | undefined): string => {
  const search = new URLSearchParams();

  const encode = (value: unknown): string => {
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const append = (key: string, value: unknown) => {
    if (value === null || value === undefined) return;
    search.append(key, encode(value));
  };

  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => append(key, item));
      return;
    }
    append(key, value);
  });

  return search.toString();
};

export const axios = Axios.create({
  baseURL: getEnv('REACT_APP_API_BASE_URL'),
  responseType: 'json',
  timeout: 60000,
  paramsSerializer: serializeQueryParams,
});

axios.interceptors.request.use((request: any) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    request.headers["Authorization"] = `Bearer ${token}`;
  }
  request.headers["Content-Type"] = "application/json";
  return request;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    // Both the terms gate and the forced-MFA-enrollment gate answer with HTTP 412. They are
    // distinguished by the X-MFA-Enrollment-Required response header: present (true) → the
    // user must set up 2FA before any /api/** call succeeds; absent → updated terms must be
    // accepted (existing behavior).
    if (status === 412) {
      const headers = error?.response?.headers ?? {};
      const enrollmentRequired =
        String(
          headers['x-mfa-enrollment-required'] ?? headers['X-MFA-Enrollment-Required'] ?? ''
        ).toLowerCase() === 'true';

      const target = enrollmentRequired ? routesConfig.mfaEnrollment : routesConfig.agreement;

      // The gate page itself issues /api calls (e.g. totpSetup on the enrollment page),
      // and those can answer 412 again — e.g. a stale session whose account already has
      // 2FA enabled elsewhere can't run setup. Redirecting to the page we're already on
      // would full-reload it and loop forever. So only navigate when we're elsewhere;
      // when already on the gate, reject so the page can surface its own error instead.
      if (window.location.pathname !== target) {
        window.location.href = target;
        return new Promise(() => {});
      }

      return Promise.reject(error);
    }

    // A 401 means the session is no longer valid (e.g. expired/invalid token). Drop the
    // stale token and send the user to sign in. Skip when already on an auth page so the
    // sign-in form can surface its own 401 (wrong credentials) without a redirect loop.
    if (status === 401 && !window.location.pathname.startsWith(routesConfig.auth)) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      window.location.href = routesConfig.signIn;
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);
